import React, { useState, useEffect } from 'react';
import { SparkleIcon, CloseIcon } from './BespokeIcons';

/**
 * SoulReminder - A prominent recurring message about connecting with one's soul
 * 
 * From Steve's wisdom: "This isn't really about an app, or exercises, or saying the 'right' words.
 * It's a state of mind. When each person reconnects to their own soul — and stays connected 
 * to the soul of everyone involved — we become receptive to solutions we couldn't see coming."
 */

const SOUL_REMINDER = {
  text: "This isn't really about an app, or exercises, or saying the \"right\" words. It's a state of mind.",
  emphasis: "When each person reconnects to their own soul — and stays connected to the soul of everyone involved — we become receptive to solutions we couldn't see coming.",
};

// Floating banner that appears periodically during the flow
export const SoulReminderBanner = ({ show, onDismiss, variant = 'default' }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    if (show) {
      // Small delay for animation
      setTimeout(() => setIsVisible(true), 100);
    } else {
      setIsVisible(false);
    }
  }, [show]);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      setIsLeaving(false);
      onDismiss?.();
    }, 300);
  };

  if (!show && !isLeaving) return null;

  const variants = {
    default: {
      bg: 'bg-gradient-to-r from-amber-900/30 via-amber-800/20 to-amber-900/30',
      border: 'border-amber-500/30',
      text: 'text-amber-100',
      emphasis: 'text-amber-300',
      glow: 'shadow-amber-500/20'
    },
    purple: {
      bg: 'bg-gradient-to-r from-purple-900/30 via-purple-800/20 to-purple-900/30',
      border: 'border-purple-500/30',
      text: 'text-purple-100',
      emphasis: 'text-purple-300',
      glow: 'shadow-purple-500/20'
    },
    rose: {
      bg: 'bg-gradient-to-r from-rose-900/30 via-rose-800/20 to-rose-900/30',
      border: 'border-rose-500/30',
      text: 'text-rose-100',
      emphasis: 'text-rose-300',
      glow: 'shadow-rose-500/20'
    }
  };

  const style = variants[variant] || variants.default;

  return (
    <div 
      className={`
        fixed top-20 left-4 right-4 z-50 max-w-xl mx-auto
        ${style.bg} backdrop-blur-md border ${style.border} rounded-2xl p-5
        shadow-lg ${style.glow}
        transition-all duration-500 ease-out
        ${isVisible && !isLeaving ? 'translate-y-0 opacity-100' : '-translate-y-8 opacity-0'}
      `}
      data-testid="soul-reminder-banner"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/10 transition-colors"
        aria-label="Dismiss"
      >
        <CloseIcon size={16} color="rgba(255,255,255,0.5)" />
      </button>
      
      <div className="flex gap-4">
        <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center flex-shrink-0">
          <SparkleIcon size={20} color="#FBBF24" className="animate-pulse" />
        </div>
        <div className="flex-1 pr-6">
          <p className={`text-sm ${style.text} mb-2 leading-relaxed`}>
            {SOUL_REMINDER.text}
          </p>
          <p className={`text-sm font-medium ${style.emphasis} leading-relaxed italic`} style={{ fontFamily: "'Playfair Display', serif" }}>
            {SOUL_REMINDER.emphasis}
          </p>
        </div>
      </div>
    </div>
  );
};

// Inline soul reminder - smaller, fits within the flow
export const SoulReminderInline = ({ show, variant = 'minimal' }) => {
  if (!show) return null;

  if (variant === 'minimal') {
    return (
      <div 
        className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg text-xs text-amber-300/80 animate-fadeIn"
        data-testid="soul-reminder-inline"
      >
        <SparkleIcon size={14} color="#FBBF24" />
        <span className="italic">Reconnect to your soul... and to theirs</span>
      </div>
    );
  }

  return (
    <div 
      className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl animate-fadeIn"
      data-testid="soul-reminder-inline-full"
    >
      <div className="flex items-start gap-3">
        <SparkleIcon size={18} color="#FBBF24" className="flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-200 text-sm leading-relaxed mb-1">
            {SOUL_REMINDER.text}
          </p>
          <p className="text-amber-300 text-sm font-medium italic" style={{ fontFamily: "'Playfair Display', serif" }}>
            {SOUL_REMINDER.emphasis}
          </p>
        </div>
      </div>
    </div>
  );
};

// Hook to manage soul reminder timing
export const useSoulReminder = (currentStep, isActive = true) => {
  const [showReminder, setShowReminder] = useState(false);
  const [reminderCount, setReminderCount] = useState(0);

  useEffect(() => {
    if (!isActive) return;

    // Show reminder at key moments:
    // - Step 2 (Feel) - after 30 seconds
    // - Step 3 (Need) - after 45 seconds  
    // - Step 4 (Bridge) - after 20 seconds
    // - Step 5 (Gold) - after 60 seconds
    
    const stepTimers = {
      2: 30000,  // Feel step - 30s
      3: 45000,  // Need step - 45s
      4: 20000,  // Bridge step - 20s
      5: 60000,  // Gold step - 60s
    };

    const timer = stepTimers[currentStep];
    if (timer && reminderCount < 3) { // Max 3 reminders per session
      const timeoutId = setTimeout(() => {
        setShowReminder(true);
        setReminderCount(prev => prev + 1);
      }, timer);

      return () => clearTimeout(timeoutId);
    }
  }, [currentStep, isActive, reminderCount]);

  const dismissReminder = () => setShowReminder(false);
  const triggerReminder = () => setShowReminder(true);

  return { showReminder, dismissReminder, triggerReminder };
};

// Full-screen soul connection moment - for transitional moments
export const SoulConnectionMoment = ({ show, onComplete, participants = [] }) => {
  const [phase, setPhase] = useState('fade-in');

  useEffect(() => {
    if (!show) {
      setPhase('fade-in');
      return;
    }

    // Animation phases
    const timers = [
      setTimeout(() => setPhase('message'), 500),
      setTimeout(() => setPhase('emphasis'), 3000),
      setTimeout(() => onComplete?.(), 7000),
    ];

    return () => timers.forEach(clearTimeout);
  }, [show, onComplete]);

  if (!show) return null;

  return (
    <div 
      className={`fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-6 transition-opacity duration-500 ${
        phase === 'fade-in' ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="soul-connection-moment"
    >
      {/* Ambient glow */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.15) 0%, transparent 60%)',
            animation: 'pulse 4s ease-in-out infinite'
          }}
        />
      </div>

      <div className="relative max-w-2xl mx-auto text-center">
        {/* Soul icon */}
        <div className={`mb-8 transition-all duration-1000 ${
          phase !== 'fade-in' ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}>
          <div className="w-20 h-20 mx-auto rounded-full bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
            <SparkleIcon size={36} color="#FBBF24" />
          </div>
        </div>

        {/* Message */}
        <div className={`transition-all duration-1000 delay-500 ${
          phase === 'message' || phase === 'emphasis' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p className="text-xl text-white/80 leading-relaxed mb-6" style={{ fontFamily: "'Figtree', sans-serif" }}>
            {SOUL_REMINDER.text}
          </p>
        </div>

        {/* Emphasis */}
        <div className={`transition-all duration-1000 ${
          phase === 'emphasis' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <p 
            className="text-2xl text-amber-300 leading-relaxed font-light italic"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {SOUL_REMINDER.emphasis}
          </p>
          
          {participants.length > 0 && (
            <div className="mt-8 flex justify-center gap-2">
              {participants.map((person, idx) => (
                <div 
                  key={person.id || idx}
                  className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-white/60 text-sm"
                >
                  {person.name || person}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Skip button */}
        <button 
          onClick={onComplete}
          className="absolute bottom-8 right-8 text-white/40 hover:text-white/60 text-sm transition-colors"
        >
          Continue →
        </button>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.15; }
          50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.25; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export { SOUL_REMINDER };
export default SoulReminderBanner;
